import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import CualappLogo from './CualappLogo';

const OnboardingRequestPage = () => {
  const [policy, setPolicy] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [showStatus, setShowStatus] = useState(false);
  const [statusEmail, setStatusEmail] = useState('');
  const [statusResult, setStatusResult] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    recommended_by: '',
    relationship: '',
    motivation: '',
  });

  useEffect(() => {
    axios.get(`${API}/onboarding/policy`).then(r => setPolicy(r.data)).catch(() => {});
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const checkStatus = async (e) => {
    e.preventDefault();
    if (!statusEmail) return;
    setStatusLoading(true);
    setStatusResult(null);
    try {
      const r = await axios.get(`${API}/onboarding/status`, { params: { email: statusEmail } });
      setStatusResult(r.data);
    } catch (err) {
      setStatusResult({
        found: false,
        status: 'error',
        message: err.response?.status === 429
          ? 'Demasiadas consultas. Espera un minuto e intenta de nuevo.'
          : (err.response?.data?.detail || 'No fue posible consultar el estado.'),
      });
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/onboarding/request`, form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'No fue posible enviar la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="onboarding-success-panel">
          <div className="flex justify-center mb-4"><CualappLogo size={72} animated={true} /></div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Solicitud recibida</h1>
          <p className="text-gray-600 mb-6">
            Tu solicitud fue registrada exitosamente. El Soberano de CUALAPP la revisara
            personalmente. Si es aprobada, recibiras un codigo de invitacion unico por el
            canal que hayas indicado.
          </p>
          <a href="/" data-testid="onboarding-back-home" className="inline-block px-5 py-2 rounded-md bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-medium">
            Volver al inicio
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-3"><CualappLogo size={80} animated={true} /></div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">CUALAPP</h1>
          <p className="text-sm text-gray-500 mt-1">Sistema de Incorporacion</p>
        </div>

        <div className="bg-white rounded-2xl shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Solicita acceso a la Beta Cerrada</h2>
          <p className="text-sm text-gray-600 mb-6">
            {policy?.requires_invite_code
              ? 'CUALAPP se encuentra en Beta Cerrada. Cada solicitud es revisada personalmente por el Soberano. Si es aprobada, recibiras un codigo unico de invitacion vinculado a tu correo.'
              : 'Registro publico abierto. Este formulario queda disponible como historia del ecosistema.'}
          </p>

          {/* Panel de consulta de estado */}
          <div className="mb-6 border border-gray-200 rounded-lg bg-gray-50">
            <button type="button" onClick={() => setShowStatus(!showStatus)}
              data-testid="onboarding-status-toggle"
              className="w-full text-left px-4 py-3 flex justify-between items-center hover:bg-gray-100 rounded-lg transition">
              <span className="text-sm font-medium text-gray-700">¿Ya enviaste una solicitud? Consulta el estado</span>
              <span className="text-gray-400 text-lg">{showStatus ? '−' : '+'}</span>
            </button>
            {showStatus && (
              <div className="px-4 pb-4 pt-2 border-t border-gray-200">
                <form onSubmit={checkStatus} className="flex flex-col sm:flex-row gap-2" data-testid="onboarding-status-form">
                  <input type="email" required placeholder="tu@correo.com"
                    value={statusEmail} onChange={(e) => setStatusEmail(e.target.value)}
                    data-testid="onboarding-status-email"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-purple-500 focus:border-purple-500" />
                  <button type="submit" disabled={statusLoading}
                    data-testid="onboarding-status-submit"
                    className="px-4 py-2 rounded-md bg-slate-700 text-white text-sm font-medium disabled:opacity-50">
                    {statusLoading ? 'Consultando...' : 'Consultar'}
                  </button>
                </form>
                {statusResult && (
                  <div className={`mt-3 p-3 rounded-md text-sm ${
                    statusResult.status === 'approved' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' :
                    statusResult.status === 'pending' ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' :
                    statusResult.status === 'info_requested' ? 'bg-blue-50 border border-blue-200 text-blue-800' :
                    statusResult.status === 'rejected' ? 'bg-gray-100 border border-gray-300 text-gray-700' :
                    statusResult.status === 'consumed' ? 'bg-purple-50 border border-purple-200 text-purple-800' :
                    'bg-gray-100 text-gray-700'
                  }`} data-testid="onboarding-status-result">
                    <p className="font-medium">{statusResult.message}</p>
                    {statusResult.rejection_reason && (
                      <p className="mt-2 text-xs italic">Motivo: {statusResult.rejection_reason}</p>
                    )}
                    {statusResult.created_at && (
                      <p className="mt-1 text-xs opacity-70">Solicitud enviada: {new Date(statusResult.created_at).toLocaleDateString('es-ES')}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="onboarding-request-form">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo *</label>
              <input name="name" required minLength={2} maxLength={120} value={form.name} onChange={handleChange}
                data-testid="onboarding-name-input"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Correo electronico *</label>
                <input name="email" type="email" required value={form.email} onChange={handleChange}
                  data-testid="onboarding-email-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefono (opcional)</label>
                <input name="phone" type="tel" value={form.phone} onChange={handleChange}
                  data-testid="onboarding-phone-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recomendado por (correo o nombre)</label>
                <input name="recommended_by" value={form.recommended_by} onChange={handleChange}
                  data-testid="onboarding-recommendedby-input"
                  placeholder="ej. juan@correo.com o Juan Perez"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vinculo con esa persona</label>
                <input name="relationship" value={form.relationship} onChange={handleChange}
                  data-testid="onboarding-relationship-input"
                  placeholder="amigo, familiar, colega..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Motivacion *</label>
              <textarea name="motivation" required minLength={10} maxLength={1000} rows={4} value={form.motivation} onChange={handleChange}
                data-testid="onboarding-motivation-input"
                placeholder="Cuentanos por que quieres formar parte de CUALAPP..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500" />
              <p className="text-xs text-gray-400 mt-1">{form.motivation.length}/1000</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm" data-testid="onboarding-error">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <a href="/" className="text-sm text-gray-500 hover:text-gray-700" data-testid="onboarding-cancel">
                Cancelar
              </a>
              <button type="submit" disabled={submitting}
                data-testid="onboarding-submit-button"
                className="px-6 py-2 rounded-md bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-medium disabled:opacity-50">
                {submitting ? 'Enviando...' : 'Enviar solicitud'}
              </button>
            </div>
          </form>

          <p className="mt-6 text-xs text-gray-400 text-center">
            Tu informacion sera tratada conforme a la Politica de Privacidad de CUALAPP.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingRequestPage;
